import * as React from 'react'
import { classnames } from '@halo/_utils/classnames'
import { PageHeader } from '@pushly/aqe/lib/components'
import { AccountDto } from '@halo/dtos/account.dto'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { AccountService } from '@halo/services/account.service'
import { Container } from 'typescript-ioc/es5'
import { AccountDataForm } from '@halo/components/account-data-form/account-data-form'
import { Tabs } from 'antd'
import { UserList } from '@halo/components/user-list/user-list'
import { DomainList } from '@halo/components/domain-list/domain-list'
import { AccountBilling } from '@halo/components/account-data-form/account-billing'
import { noop } from '@pushly/aqe/lib/utils/noop'
import { ORGANIZATIONS_ROUTE } from '@halo/portals/post-auth/accounts'
import { RouteComponentProps } from 'react-router-dom'
import { Crumb, makeCrumbClick } from '@halo/_utils/breadcrumb'

interface IProps extends RouteComponentProps<{ accountId: string }, any, { account?: AccountDto }> {}

interface IAccountState {
    loading: boolean
    account?: AccountDto
}

export class Account extends React.Component<IProps, IAccountState> {
    protected appState: AppState
    protected appService: AppService
    protected accountService: AccountService

    public constructor(props: IProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.accountService = Container.get(AccountService)

        this.state = {
            loading: !props.location.state?.account,
            account: props.location.state?.account,
        }
    }

    public componentDidMount() {
        if (!this.state.account) {
            this.loadRequestedAccount()
        }
    }

    public render() {
        const onCrumbClick = makeCrumbClick(this.appService)
        const crumbs: Crumb[] | undefined = this.state.account
            ? [
                  { title: 'Organizations', href: ORGANIZATIONS_ROUTE, onClick: onCrumbClick },
                  { title: this.state.account.name },
              ]
            : undefined

        return (
            <div className={classnames('page', 'single-account')}>
                <div className={classnames('page-wrapper')}>
                    <PageHeader title="Organization" onTitleSet={noop} breadcrumb={crumbs} />

                    <Tabs
                        size="small"
                        animated={{ inkBar: false, tabPane: false }}
                        tabPosition="top"
                        activeKey={this.currentTabKey}
                        onChange={this.setCurrentTabKey}
                    >
                        <Tabs.TabPane key="settings" tab="Settings">
                            <AccountDataForm accountId={this.requestedAccountId} />
                        </Tabs.TabPane>
                        {this.appState.currentUser?.isInternalUser && (
                            <Tabs.TabPane key="billing" tab="Billing">
                                <AccountBilling accountId={this.requestedAccountId} />
                            </Tabs.TabPane>
                        )}
                        <Tabs.TabPane key="domains" tab="Domains">
                            {this.state.account && <DomainList options={{ account: this.state.account }} />}
                        </Tabs.TabPane>
                        <Tabs.TabPane key="users" tab="Users">
                            {this.state.account && <UserList options={{ account: this.state.account }} />}
                        </Tabs.TabPane>
                    </Tabs>
                </div>
            </div>
        )
    }

    protected get requestedAccountId(): number {
        let accountId: number
        try {
            accountId = parseInt(this.props.match.params.accountId, 10)
        } catch {}
        return accountId
    }

    protected get currentTabKey(): string {
        let key = 'settings'

        if (this.props.location.hash) {
            key = this.props.location.hash.replace('#', '').toLowerCase() || key
        }

        return key
    }

    protected setCurrentTabKey = (key: string): void => {
        this.appService.route(`${this.props.location.pathname}#${key}`)
    }

    protected async loadRequestedAccount() {
        this.setState({ loading: true })

        try {
            const account = await this.accountService.fetchById(this.requestedAccountId)
            if (account) {
                this.setState({ account })
            }
        } catch {}

        this.setState({ loading: false })
    }
}
